import "./Users.scss";
import { useState, useEffect } from "react";
import { InputAdornment, TextField, useMediaQuery } from "@mui/material";
import {  useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { HomeColumns } from "../../SourceData";
import { ConfigProvider, DatePicker } from "antd";
import ShowUser from "./../../assets/show-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../utils/api";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import MessageModals from "../../components/MessageModals/MessageModals";
const { RangePicker } = DatePicker;

const Users = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDelete, setOpenDelete] = useState(false);
  const [deletedItem, setDeletedItem] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  dayjs.locale("ar");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Users data state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Fetch users data
  const fetchUsers = async () => {
    if (!hasPermission(24)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.q = searchTerm;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      // Log the request parameters for debugging

      const response = await api.get("/admin/users", { params });

      if (response.data.status === "success") {
        setUsers(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch users when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, searchTerm, dateRange]);

  const handleActive = async (id) => {
    if (!hasPermission(25)) {
      toast.error("ليس لديك صلاحية تغير حالة المستخدم");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/users/${id}/toggleState`);
      toast.success("تم تغيير الحالة بنجاح");
      fetchUsers();
    } catch (error) {
      console.error("Error toggling category status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (rowData) => {
    if (!hasPermission(26)) {
      toast.error("ليس لديك صلاحية لحذف المسؤول");
      return;
    }
    setDeletedItem(rowData);
    setOpenDelete(true);
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "right",
      align: "left",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="cell-action">
          <div
            className="cell-btn"
            onClick={() => {
              if (hasPermission(28)) {
                navigate(`/users/${params.row._id}`);
              } else {
                toast.error("ليس لديك صلاحية لعرض التفاصيل");
              }
            }}
          >
            <img src={ShowUser} alt="عرض" />
          </div>

          <div
            className="cell-btn"
            onClick={() => handleDeleteClick(params.row)}
          >
            <img src={DeleteUser} alt="حذف" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          المستخدمين <span className="count-badge">{totalCount || 0}</span>
        </p>
        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث بالاسم او رقم الهاتف"
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <ConfigProvider locale={ar_EG} direction="rtl">
              <RangePicker
                format={"DD MMM"}
                style={{
                  width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                }}
                placeholder={["من", "الي"]}
                className="range-picker"
                onChange={handleDateRangeChange}
                value={dateRange}
                popupStyle={{ direction: "rtl" }}
                inputReadOnly={true}
              />
            </ConfigProvider>
          </div>
        </div>
      </div>

      <div className="container">
        {hasPermission(24) && (
          <DataGrid
            sx={{ backgroundColor: "white", width: "100%" }}
            autoHeight={true}
            columns={HomeColumns.concat(actionColumn)}
            rows={users.map((user, index) => ({
              ...user,
              // Generate unique id if not present
              id: user._id || index,
              // Add index for numbering
              index: (page - 1) * pageSize + index + 1,
            }))}
            disableVirtualization
            disableColumnFilter
            disableColumnMenu
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            initialState={{
              pagination: {
                paginationModel: { pageSize, page: page - 1 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            onPaginationModelChange={(model) => {
              // Handle page change
              if (model.page !== page - 1) {
                setPage(model.page + 1);
              }

              // Handle page size change
              if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(1); // Reset to first page when changing page size
              }
            }}
            getRowClassName={(params) => "data-grid-row"}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: "مستخدم في الصفحة",
              },
            }}
          />
        )}
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        deleteMessage="تم حذف العميل بنجاح"
        route={`/admin/users/${deletedItem?._id}`}
        onClose={() => {
          setOpenDelete(false);
          setDeletedItem(null);
        }}
        onDataChange={fetchUsers}
      />
    </div>
  );
};

export default Users;
